import React, { useState, useEffect, useLayoutEffect, useRef, lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from 'react-router-dom';
import Palettes from './resources/UtilityJsons/Palettes.json';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import './index.css';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { Box, CircularProgress, Skeleton } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import ScreenSizeContext, { useScreenSize } from './context/ScreenSizeContext';
import AnalyticsV2Provider from './analytics/AnalyticsV2Provider';

// Componenti sempre presenti (caricati subito)
import Header from './components/Global/Header/Header';
const Footer = lazy(() => import('./components/Global/Footer/Footer'));
const MobileDonationBanner = lazy(
  () => import('./components/Global/MobileDonationBanner')
);

// Pagine caricate in modo lazy (code splitting)
const ActivityFeedPage = lazy(() => import('./pages/ActivityFeedPage/ActivityFeedPage'));
const ExplorePage = lazy(() => import('./pages/ExplorePage/ExplorePage'));
const ScreenerPage = lazy(() => import('./pages/ScreenerPage/ScreenerPage'));
const StocksDashboardPage = lazy(() => import('./pages/stocksDashboardPage/stocksDashboardPage'));
const StockPage = lazy(() => import('./pages/stockPage/stockPage'));
const StatsBreakdownPage = lazy(() => import('./pages/infoPages/statsBreakdownPage'));
const DisclaimerPage = lazy(() => import('./pages/DisclaimerPage/DisclaimerPage'));
const InvestorPage = lazy(() => import('./pages/InvestorPage/InvestorPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage/NotFoundPage'));
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage/ComingSoonPage'));
const StartAnalysisPage = lazy(() => import('./pages/StartAnalysisPage/StartAnalysisPage'));
const MyAnalysesPage = lazy(() => import('./pages/MyAnalysesPage/MyAnalysesPage'));
const MySpacePage = lazy(() => import('./pages/MySpacePage/MySpacePage'));
const CommunityValuationPage = lazy(
  () => import('./pages/CommunityValuationPage/CommunityValuationPage')
);
const MainMetricsBoxExplanation = lazy(() => import('./pages/ExplanationPages/MainMetricsBoxExplanation'));
const EarningsBoxExplanation = lazy(() => import('./pages/ExplanationPages/EarningsBoxExplanation'));
const BalanceSheetBoxExplanation = lazy(() => import('./pages/ExplanationPages/BalanceSheetBoxExplanation'));
const MarginsBoxExplanation = lazy(() => import('./pages/ExplanationPages/MarginsBoxExplanation'));
const CashFlowInOutBoxExplanation = lazy(() => import('./pages/ExplanationPages/CashFlowInOutBoxExplanation'));
const StatisticsBoxExplanation = lazy(() => import('./pages/ExplanationPages/StatisticsBoxExplanation/StatisticsBoxExplanation'));
const WatchListPage = lazy(() => import('./pages/WatchListPage/WatchListPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage/TermsPage'));
const CookiesPolicyPage = lazy(() => import('./pages/CookiesPolicyPage/CookiesPolicyPage'));
const AboutPage = lazy(() => import('./pages/AboutPage/AboutPage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage/ContactsPage'));
const GoogleAuthCallbackPage = lazy(
  () => import('./pages/GoogleAuthCallbackPage/GoogleAuthCallbackPage')
);

const PageLoader: React.FC<{ pathname?: string }> = ({ pathname = '' }) => {
  if (pathname === '/' || pathname === '/feed') {
    return (
      <Box
        aria-label="Loading activity feed"
        sx={{
          minHeight: '100svh',
          bgcolor: '#f3f5f4',
          px: { xs: 1.5, sm: 3 },
          pt: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ maxWidth: 730, mx: 'auto' }}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Box
              key={index}
              sx={{
                bgcolor: '#fff',
                border: '1px solid #e0e7e3',
                borderRadius: 3.5,
                p: 2.5,
                mb: 2,
                minHeight: 190,
              }}
            >
              <Skeleton width={150} height={28} animation="wave" />
              <Skeleton width="78%" height={34} animation="wave" />
              <Skeleton
                variant="rounded"
                height={62}
                sx={{ mt: 1.2 }}
                animation="wave"
              />
              <Skeleton width="45%" height={36} sx={{ mt: 1.4 }} animation="wave" />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (pathname === '/screener') {
    return (
      <Box sx={{ px: { xs: 0, sm: 2 }, py: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, px: { xs: 1, sm: 0 } }}>
          {[84, 96, 74].map(width => (
            <Skeleton
              key={width}
              variant="rounded"
              width={width}
              height={32}
              animation="wave"
            />
          ))}
        </Box>
        <Box sx={{ bgcolor: '#fff', borderRadius: { xs: 0, sm: 3 }, p: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '42px 1fr 1fr', md: '42px 1.4fr repeat(6, 1fr)' },
              gap: 2,
              mb: 1.5,
            }}
          >
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="text"
                height={26}
                animation="wave"
                sx={{ display: { xs: index < 3 ? 'block' : 'none', md: 'block' } }}
              />
            ))}
          </Box>
          {Array.from({ length: 7 }).map((_, row) => (
            <Box
              key={row}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '42px 1fr 1fr', md: '42px 1.4fr repeat(6, 1fr)' },
                alignItems: 'center',
                gap: 2,
                py: 1.25,
                borderTop: '1px solid #eee',
              }}
            >
              <Skeleton variant="rounded" width={30} height={30} animation="wave" />
              {Array.from({ length: 7 }).map((_, column) => (
                <Skeleton
                  key={column}
                  variant="text"
                  height={24}
                  animation="wave"
                  sx={{ display: { xs: column < 2 ? 'block' : 'none', md: 'block' } }}
                />
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  const stockMatch = pathname.match(/^\/stocks\/([^/]+)/);
  if (stockMatch) {
    const ticker = decodeURIComponent(stockMatch[1]);
    return (
      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 3 }, py: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minHeight: 104,
            bgcolor: '#fff',
            borderRadius: 3,
            p: 2,
          }}
        >
          <img
            src={`/stock-logos/icon-72/${encodeURIComponent(
              ticker.trim().toUpperCase()
            )}.png?v=3`}
            alt={`${ticker} logo`}
            width={56}
            height={56}
            decoding="async"
            onError={event => {
              event.currentTarget.style.visibility = 'hidden';
            }}
            style={{ objectFit: 'contain' }}
          />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ fontSize: '1.25rem', fontWeight: 800 }}>{ticker}</Box>
            <Skeleton variant="text" width={150} height={24} animation="wave" />
          </Box>
          <Skeleton variant="text" width={110} height={40} animation="wave" />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, my: 1.5 }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={56} animation="wave" />
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Skeleton variant="rounded" height={190} animation="wave" />
          <Skeleton variant="rounded" height={190} animation="wave" />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
      <CircularProgress size={36} />
    </Box>
  );
};

// Typography Design System – custom variants
declare module '@mui/material/styles' {
  interface TypographyVariants {
    Title: React.CSSProperties;
    CardTitle: React.CSSProperties;
    BigText: React.CSSProperties;
    MainText: React.CSSProperties;
    MainInfo: React.CSSProperties;
    MainInfoSmall: React.CSSProperties;
    Info: React.CSSProperties;
    LabelMedium: React.CSSProperties;
    LabelSmall: React.CSSProperties;
    SimpleText: React.CSSProperties;
    SimpleTextSmall: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    Title?: React.CSSProperties;
    CardTitle?: React.CSSProperties;
    BigText?: React.CSSProperties;
    MainText?: React.CSSProperties;
    MainInfo?: React.CSSProperties;
    MainInfoSmall?: React.CSSProperties;
    Info?: React.CSSProperties;
    LabelMedium?: React.CSSProperties;
    LabelSmall?: React.CSSProperties;
    SimpleText?: React.CSSProperties;
    SimpleTextSmall?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    Title: true;
    CardTitle: true;
    BigText: true;
    MainText: true;
    MainInfo: true;
    MainInfoSmall: true;
    Info: true;
    LabelMedium: true;
    LabelSmall: true;
    SimpleText: true;
    SimpleTextSmall: true;
  }
}

// Crea il queryClient con impostazioni di default
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const theme = createTheme({
  palette: {
    primary: {
      main: Palettes.MainColors.Primary,
    },
  },
  typography: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    // Title: h1 – Testo nero bold grande
    Title: {
      fontWeight: 600,
      fontSize: '20px',
      lineHeight: 1.1,
      color: Palettes.UtilityColor.textPrimary,
      marginBottom: 0.5,
    },
    // CardTitle: h2 – Testo nero medium
    CardTitle: {
      fontWeight: 800,
      fontSize: '20px',
      color: Palettes.UtilityColor.textPrimary,
    },
    // BigText: p – Testo nero bold
    BigText: {
      fontWeight: 700,
      fontSize: '26px',
      lineHeight: 1.4,
      color: Palettes.UtilityColor.textPrimary,
    },
    // MainText: p – Testo nero bold
    MainText: {
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: 1.5,
      color: Palettes.UtilityColor.textPrimary,
    },
    // MainInfo: p – Testo grigio bold
    MainInfo: {
      fontWeight: 700,
      fontSize: '13px',
      lineHeight: 1.5,
      color: Palettes.UtilityColor.textSecondary,
    },
    // MainInfoSmall: p – Testo grigio bold piccolo
    MainInfoSmall: {
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: 1.4,
      color: Palettes.UtilityColor.textSecondary,
    },
    // Info: p – Testo grigio
    Info: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: 1.5,
      color: Palettes.UtilityColor.textSecondary,
    },
    // LabelMedium: span – Testo grigio medium
    LabelMedium: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: 1.4,
      color: Palettes.UtilityColor.textSecondary,
    },
    // LabelSmall: span – Testo grigio piccolo
    LabelSmall: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: 1.3,
      color: Palettes.UtilityColor.textSecondary,
      marginLeft: 6,
      marginRight: 6,
    },
    // SimpleText: p – Testo nero
    SimpleText: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: 1.5,
      color: Palettes.UtilityColor.textPrimary,
    },
    // SimpleTextSmall: p – Testo nero piccolo (12px)
    SimpleTextSmall: {
      fontWeight: 400,
      fontSize: '13px',
      lineHeight: 1.6,
      color: Palettes.UtilityColor.textPrimary,
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          Title: 'h1',
          CardTitle: 'h2',
          BigText: 'p',
          MainText: 'p',
          MainInfo: 'p',
          MainInfoSmall: 'p',
          Info: 'p',
          LabelMedium: 'span',
          LabelSmall: 'span',
          SimpleText: 'p',
          SimpleTextSmall: 'p',
        },
      },
    },
  },
});

// Imposta le variabili CSS da Palettes
document.documentElement.style.setProperty(
  '--palette-color0',
  Palettes.Palette1.color0
);
document.documentElement.style.setProperty(
  '--palette-color1',
  Palettes.Palette1.color1
);
document.documentElement.style.setProperty(
  '--palette-color2',
  Palettes.Palette1.color2
);
document.documentElement.style.setProperty(
  '--palette-color3',
  Palettes.Palette1.color3
);
document.documentElement.style.setProperty(
  '--palette-color4',
  Palettes.Palette1.color4
);
document.documentElement.style.setProperty(
  '--palette-color5',
  Palettes.Palette1.color5
);
document.documentElement.style.setProperty(
  '--appBackGround',
  Palettes.MainColors.AppBackground
);

const scrollDocumentToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  document.scrollingElement?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  document.getElementById('app-content')?.scrollTo({
    top: 0,
    left: 0,
    behavior: 'auto',
  });
};

const ScrollToTop: React.FC = () => {
  const { pathname, key } = useLocation();
  const previousPathname = useRef<string | null>(null);

  useLayoutEffect(() => {
    const stayedInsideAnalysis =
      previousPathname.current === pathname &&
      pathname.toLowerCase().startsWith('/start-analysis/');
    previousPathname.current = pathname;

    // Changing DCF parameters updates only the query string. Keep the user's
    // position while they remain on the same analysis workspace.
    if (stayedInsideAnalysis) return;

    // If we are returning to the Screener and a previous scroll is saved, skip forcing scrollTop
    try {
      const normalizedPath = pathname.toLowerCase();
      const isScreener =
        normalizedPath.startsWith('/screener') ||
        normalizedPath === '/explore/screener';
      const hasSavedScroll = !!sessionStorage.getItem('screenerScrollY');
      if (isScreener && hasSavedScroll) {
        return; // let the Screener page handle restoring its own scroll
      }
    } catch {}

    scrollDocumentToTop();
    const frameId = requestAnimationFrame(() => {
      scrollDocumentToTop();
    });
    const timeoutId = window.setTimeout(scrollDocumentToTop, 0);

    return () => {
      cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [pathname, key]);
  return null;
};

// Componente Root per verificare lo stato di autenticazione e dimensione schermo

const Root: React.FC = () => {
  const MOBILE_BREAKPOINT = 1000;
  const [loggedIn] = useState(() => !!localStorage.getItem('token'));
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= MOBILE_BREAKPOINT
  );

  useEffect(() => {
    let rafId: number;
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
      });
    };
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    mq.addEventListener('change', handleResize);
    return () => {
      cancelAnimationFrame(rafId);
      mq.removeEventListener('change', handleResize);
    };
  }, []);

  // Ensure the browser does not restore scroll positions between route changes
  useEffect(() => {
    try {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual' as any;
      }
    } catch {}
  }, []);

  return (
    <ScreenSizeContext.Provider value={{ isMobile }}>
      <App loggedIn={loggedIn} />
    </ScreenSizeContext.Provider>
  );
};

const App: React.FC<{ loggedIn: boolean }> = () => {
  const location = useLocation();
  const { isMobile } = useScreenSize();
  const [mountMobilePrompts, setMountMobilePrompts] = useState(false);

  const hideChrome = location.pathname.startsWith('/start-analysis/');
  const isDashboardRoute =
    location.pathname === '/' ||
    location.pathname === '' ||
    location.pathname.startsWith('/explore') ||
    location.pathname === '/feed' ||
    location.pathname === '/activity';

  useEffect(() => {
    if (!isMobile) {
      setMountMobilePrompts(false);
      return;
    }
    const timer = window.setTimeout(() => setMountMobilePrompts(true), 5_000);
    return () => window.clearTimeout(timer);
  }, [isMobile]);

  return (
    <Box
      sx={{
        background: Palettes.MainColors.AppBackground,
        minHeight: '100svh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {!hideChrome && <Header />}
      <Box
        component="main"
        sx={{
          flex: '1 0 auto',
          minHeight: hideChrome
            ? '100svh'
            : 'calc(100svh - var(--app-header-current-height, 64px))',
          maxWidth: isDashboardRoute || hideChrome ? 'none' : 1440,
          width: '100%',
          mx: isDashboardRoute || hideChrome ? 0 : 'auto',
          boxSizing: 'border-box',
          mt: 0,
          paddingBottom: hideChrome
            ? 0
            : 'calc(var(--app-bottom-nav-current-height, 0px) + env(safe-area-inset-bottom, 0px) + 16px)',
        }}
        id="app-content"
      >
        <Suspense fallback={<PageLoader pathname={location.pathname} />}>
        <Routes>
          <Route path="/" element={<Navigate to="/feed" replace />} />
          <Route
            path="/explore/screener"
            element={<Navigate to="/screener" replace />}
          />
          <Route path="/explore/*" element={<ExplorePage />} />
          <Route path="/feed" element={<ActivityFeedPage />} />
          <Route path="/activity" element={<Navigate to="/feed" replace />} />
          <Route path="/Dashboard" element={<Navigate to="/explore" replace />} />
          <Route path="/dashboard" element={<Navigate to="/explore" replace />} />
          <Route path="stocks" element={<StocksDashboardPage />} />
          <Route
            path="stocks/stocksDashboard"
            element={<Navigate to="/stocks" replace />}
          />
          <Route path="stocks/:ticker" element={<StockPage />} />
          <Route path="stocks/:ticker/:seoSection" element={<StockPage />} />
          <Route path="/screener" element={<ScreenerPage />} />
          <Route path="stats/breakdown" element={<StatsBreakdownPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/cookies" element={<CookiesPolicyPage />} />
          <Route path="/investors" element={<Navigate to="/explore/big-investors" replace />} />
          <Route path="/investors/:name/:cik" element={<InvestorPage />} />
          <Route path="/insiders" element={<Navigate to="/explore/insiders" replace />} />
          <Route
            path="/start-analysis/:ticker"
            element={<StartAnalysisPage />}
          />
          <Route path="/my-analyses" element={<MyAnalysesPage />} />
          <Route path="/my-space/*" element={<MySpacePage />} />
          <Route
            path="/community/dcf/:id"
            element={<CommunityValuationPage />}
          />
          <Route path="/coming-soon" element={<ComingSoonPage />} />
          {/* Explanation pages */}
          <Route
            path="/explanation/MainMetricsBox"
            element={<MainMetricsBoxExplanation />}
          />
          <Route
            path="/explanation/MainMetrics"
            element={<MainMetricsBoxExplanation />}
          />
          <Route
            path="/explanation/EarningsBox"
            element={<EarningsBoxExplanation />}
          />
          <Route
            path="/explanation/BalanceSheetBox"
            element={<BalanceSheetBoxExplanation />}
          />
          <Route
            path="/explanation/MarginsBox"
            element={<MarginsBoxExplanation />}
          />
          <Route
            path="/explanation/CashFlowInOutBox"
            element={<CashFlowInOutBoxExplanation />}
          />
          <Route
            path="/explanation/StatisticsBox"
            element={<StatisticsBoxExplanation />}
          />
          <Route path="*" element={<NotFoundPage />} />
          <Route path="/watchlist" element={<WatchListPage />} />
          <Route
            path="/auth/google/callback"
            element={<GoogleAuthCallbackPage />}
          />
        </Routes>
        </Suspense>
        {/* Mobile donation banner (appears every ~2 minutes) */}
        {isMobile && mountMobilePrompts && (
          <Suspense fallback={null}>
            <MobileDonationBanner />
          </Suspense>
        )}
      </Box>
      {!hideChrome && (
        <Suspense
          fallback={
            <Box
              component="footer"
              aria-hidden="true"
              sx={{
                minHeight: { xs: 420, sm: 300 },
                bgcolor: Palettes.MainColors.Header,
                flexShrink: 0,
              }}
            />
          }
        >
          <Footer />
        </Suspense>
      )}
    </Box>
  );
};

const rootElement = document.getElementById('root');
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ScrollToTop />
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <AuthProvider>
              <AnalyticsV2Provider />
              <Root />
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>
  );
}

serviceWorkerRegistration.register();
